import axios, {
  AxiosInstance,
  AxiosRequestConfig as RequestConfig,
} from 'axios';
import {
  API_ERROR_DEFAULT_RATE_LIMIT_DELAY_MS,
  API_ERROR_MAX_RETRIES,
  API_ERROR_RETRY_BASE_DELAY_MS,
} from '@/data/constants';

export type RetryStrategy = 'exponential' | 'fixed';

export interface AxiosRequestConfig extends RequestConfig {
  _retryCount?: number;
  retry?: boolean;
  // 'exponential' backs off 400ms, 800ms, 1600ms...
  // 'fixed' retries immediately with no delay.
  // Defaults to 'exponential' when omitted.
  retryStrategy?: RetryStrategy;
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getRateLimitDelay = (error: any): number => {
  const retryAfterHeader = error.response?.headers?.['retry-after'];
  if (!retryAfterHeader) return API_ERROR_DEFAULT_RATE_LIMIT_DELAY_MS;

  const numericValue = parseFloat(retryAfterHeader);
  if (!Number.isNaN(numericValue)) {
    return numericValue * 1000;
  }

  // Retry-After can also be an HTTP date
  const dateValue = new Date(retryAfterHeader).getTime();
  if (!Number.isNaN(dateValue)) {
    return Math.max(dateValue - Date.now(), 0);
  }

  return API_ERROR_DEFAULT_RATE_LIMIT_DELAY_MS;
};

const getRetryDelay = (
  config: AxiosRequestConfig,
  retryCount: number,
): number => {
  const strategy = config.retryStrategy ?? 'exponential';
  if (strategy === 'fixed') return API_ERROR_RETRY_BASE_DELAY_MS;

  const exponential = API_ERROR_RETRY_BASE_DELAY_MS * 2 ** retryCount;
  // Jitter: multiply by a random factor between 1x and 1.5x so
  // simultaneous clients don't all retry at the exact same moment.
  const jitterMultiplier = 1 + Math.random() * 0.5;
  return exponential * jitterMultiplier;
};

// A dedicated axios instance so the retry interceptor doesn't affect
// any other axios usage in the app.
export const createAxiosHttpClient = (): AxiosInstance => {
  const client = axios.create();

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error.config as AxiosRequestConfig | undefined;

      // No config means axios couldn't even build the request; nothing to retry.
      if (!config) return Promise.reject(error);

      // Respect aborts/cancellations — never retry these.
      if (isAbortedRequestError(error)) return Promise.reject(error);

      // Per-request opt-in/out.
      if (!config.retry) return Promise.reject(error);

      const status = error.response?.status;
      const isRateLimited = status === 429;
      const isRetryableStatus =
        status === undefined || (status >= 500 && status < 600);
      const retryable = isRetryableStatus || isRateLimited;
      if (!retryable) return Promise.reject(error);

      config._retryCount = config._retryCount ?? 0;
      if (config._retryCount >= API_ERROR_MAX_RETRIES) {
        return Promise.reject(error);
      }
      config._retryCount += 1;

      const waitMs = isRateLimited
        ? getRateLimitDelay(error)
        : getRetryDelay(config, config._retryCount);

      await delay(waitMs);

      // Bail out if the caller aborted while we were waiting to retry.
      if (config.signal instanceof AbortSignal && config.signal.aborted) {
        return Promise.reject(error);
      }

      return client(config);
    },
  );

  return client;
};

const ERR_CANCELED = 'ERR_CANCELED';
const CanceledError = 'CanceledError';
const AbortError = 'AbortError';

export const HTTP_ERRORS = {
  ERR_CANCELED,
  CanceledError,
  AbortError,
};

export const isAbortedRequestError = (error: any): boolean => {
  return (
    axios.isCancel(error) ||
    error?.code === ERR_CANCELED ||
    error?.name === CanceledError ||
    error?.name === AbortError
  );
};
